import * as uuid from 'uuid';
import { ScannerProtos } from './scanner_protos';
import { ScannerConfig, ScannerConfigV2 } from './entities';
import { FilterObject } from '@benzinga/quotes-v3-fields-manager';
import { DefaultTableParameters } from '@benzinga/ag-grid-utils';

export const DEFAULT_SCANNER_FILTERS: FilterObject[] = [
  { field: 'subtype', operator: 'in', parameters: ['ADR', 'COMMON_SHARE', 'ETF'] },
];

export const DEFAULT_SCANNER_CONFIG: ScannerConfig = {
  filters: DEFAULT_SCANNER_FILTERS,
  limit: 100,
  refreshInterval: 60,
  sortDir: ScannerProtos.SortDir.DESC,
  sortField: 'changePercent',
  source: 'stocks',
  tableParameters: {
    ...DefaultTableParameters,
    columns: [
      { colId: 'symbol', hide: false, pinned: 'left', width: 100 },
      { colId: 'price', hide: false, width: 120 },
      { colId: 'change', hide: false, width: 120 },
      { colId: 'changePercent', hide: false, width: 100 },
      { colId: 'marketCap', hide: false, width: 120 },
      { colId: 'dayVolume', hide: false, width: 120 },
    ],
  },
  tags: [],
};

export const SCANNER_PRESET: ScannerConfig[] = [
  {
    ...DEFAULT_SCANNER_CONFIG,
    name: 'Default',
    uuid: uuid.v4(),
  },
  {
    description:
      'Company gainers, regardless of session with a market-cap over 1b. This excludes ETFs, stocks under $2, and ' +
      'day volume below 10,000',
    filters: [
      { field: 'marketCap', operator: 'bt', parameters: ['1000000000', ''] },
      { field: 'price', operator: 'bt', parameters: ['2', ''] },
      { field: 'dayVolume', operator: 'bt', parameters: ['10000', ''] },
    ],
    limit: 100,
    name: 'Overall Gainers',
    refreshInterval: 10,
    sortDir: ScannerProtos.SortDir.DESC,
    sortField: 'changePercent',
    source: 'stocks',
    tableParameters: {
      ...DefaultTableParameters,
      columns: [
        { colId: 'symbol', hide: false, width: 100 },
        { colId: 'name', hide: false, width: 100 },
        { colId: 'price', hide: false, width: 120 },
        { colId: 'change', hide: false, width: 120 },
        { colId: 'changePercent', hide: false, width: 100 },
        { colId: 'dayVolume', hide: false, width: 100 },
        { colId: 'shareFloat', hide: false, width: 100 },
        { colId: 'sharesShortPercentOfFloat', hide: false, width: 100 },
        { colId: 'sharesOutstanding', hide: false, width: 100 },
        { colId: 'marketCap', hide: false, width: 100 },
        { colId: 'gicsSectorName', hide: false, width: 100 },
        { colId: 'gicsIndustryName', hide: false, width: 100 },
      ],
    },
    tags: [],
    uuid: uuid.v4(),
  },
  {
    description:
      'Gainers and Losers moving at least 2% over the past 5 minutes (rolling window), with a minimum market cap ' +
      'of 2 billion. *NOTE, the last column show the actual 5 minute change %.',
    filters: [
      { field: 'changePercent5Minute', operator: 'bt', parameters: ['2', ''] },
      { field: 'marketCap', operator: 'bt', parameters: ['2000000000', ''] },
      { field: 'dayVolume', operator: 'bt', parameters: ['50000', ''] },
    ],
    limit: 100,
    name: '5 Minute Movers',
    refreshInterval: 10,
    sortDir: ScannerProtos.SortDir.DESC_ABS,
    sortField: 'changePercent5Minute',
    source: 'stocks',
    tableParameters: {
      ...DefaultTableParameters,
      columns: [
        { colId: 'symbol', hide: false, width: 100 },
        { colId: 'name', hide: false, width: 100 },
        { colId: 'price', hide: false, width: 120 },
        { colId: 'change', hide: false, width: 120 },
        { colId: 'changePercent', hide: false, width: 100 },
        { colId: 'dayVolume', hide: false, width: 100 },
        { colId: 'shareFloat', hide: false, width: 100 },
        { colId: 'sharesShortPercentOfFloat', hide: false, width: 100 },
        { colId: 'sharesOutstanding', hide: false, width: 100 },
        { colId: 'marketCap', hide: false, width: 100 },
        { colId: 'gicsSectorName', hide: false, width: 100 },
        { colId: 'gicsIndustryName', hide: false, width: 100 },
      ],
    },
    tags: [],
    uuid: uuid.v4(),
  },
  {
    description:
      'Mid-cap movers since the Open. Note that data will populate after the open (9:30am). ' +
      'Filters for market-cap between 2 and 10 billion, minimum price of $5, and day volume over 10,000.',
    filters: [
      { field: 'price', operator: 'bt', parameters: ['5', ''] },
      { field: 'marketCap', operator: 'bt', parameters: ['2000000000', '10000000000'] },
      { field: 'dayVolume', operator: 'bt', parameters: ['10000', ''] },
    ],
    limit: 100,

    name: 'Mid-Cap movers from Open',
    refreshInterval: 10,
    sortDir: ScannerProtos.SortDir.DESC,
    sortField: 'changePercentOpen',
    source: 'stocks',
    tableParameters: {
      ...DefaultTableParameters,
      columns: [
        { colId: 'symbol', hide: false, width: 100 },
        { colId: 'name', hide: false, width: 100 },
        { colId: 'price', hide: false, width: 120 },
        { colId: 'change', hide: false, width: 120 },
        { colId: 'changePercent', hide: false, width: 100 },
        { colId: 'dayVolume', hide: false, width: 100 },
        { colId: 'shareFloat', hide: false, width: 100 },
        { colId: 'sharesShortPercentOfFloat', hide: false, width: 100 },
        { colId: 'sharesOutstanding', hide: false, width: 100 },
        { colId: 'marketCap', hide: false, width: 100 },
        { colId: 'gicsSectorName', hide: false, width: 100 },
        { colId: 'gicsIndustryName', hide: false, width: 100 },
      ],
    },
    tags: [],
    uuid: uuid.v4(),
  },
  {
    description:
      'Real-time movers after-hours. Filters out instruments under $2, with less than 10,000 volume on the day, ' +
      'and a minimum Chg % (Close) threshold of 1%. This will not return results until after the close (4pm).',
    filters: [
      { field: 'price', operator: 'bt', parameters: ['2', ''] },
      { field: 'changePercentClose', operator: 'bt', parameters: ['1', ''] },
      { field: 'dayVolume', operator: 'bt', parameters: ['10000', ''] },
    ],
    limit: 100,
    name: 'Real-time After Hours Gainers',
    refreshInterval: -2,
    sortDir: ScannerProtos.SortDir.DESC,
    sortField: 'changePercentClose',
    source: 'stocks',
    tableParameters: {
      ...DefaultTableParameters,
      columns: [
        { colId: 'symbol', hide: false, width: 100 },
        { colId: 'name', hide: false, width: 100 },
        { colId: 'price', hide: false, width: 120 },
        { colId: 'change', hide: false, width: 120 },
        { colId: 'changePercent', hide: false, width: 100 },
        { colId: 'dayVolume', hide: false, width: 100 },
        { colId: 'shareFloat', hide: false, width: 100 },
        { colId: 'sharesShortPercentOfFloat', hide: false, width: 100 },
        { colId: 'sharesOutstanding', hide: false, width: 100 },
        { colId: 'marketCap', hide: false, width: 100 },
        { colId: 'gicsSectorName', hide: false, width: 100 },
        { colId: 'gicsIndustryName', hide: false, width: 100 },
      ],
    },
    tags: [],
    uuid: uuid.v4(),
  },
  {
    description:
      'Large-Cap Gainers and Losers since the previous close, with a lot of columns selected. ' +
      'Minimal filters to exclude ETFs, and very low volume and low prices stocks',
    filters: [
      { field: 'dayVolume', operator: 'bt', parameters: ['100000', ''] },
      { field: 'marketCap', operator: 'bt', parameters: ['10000000000', '200000000000'] },
    ],
    limit: 100,
    name: 'Large-Cap Gainers, many Columns',
    refreshInterval: 10,
    sortDir: ScannerProtos.SortDir.DESC,
    sortField: 'changePercent',
    source: 'stocks',
    tableParameters: {
      ...DefaultTableParameters,
      columns: [
        { colId: 'symbol', hide: false, width: 100 },
        { colId: 'name', hide: false, width: 100 },
        { colId: 'price', hide: false, width: 120 },
        { colId: 'change', hide: false, width: 120 },
        { colId: 'changePercent', hide: false, width: 100 },
        { colId: 'dayVolume', hide: false, width: 100 },
        { colId: 'shareFloat', hide: false, width: 100 },
        { colId: 'sharesShortPercentOfFloat', hide: false, width: 100 },
        { colId: 'sharesOutstanding', hide: false, width: 100 },
        { colId: 'marketCap', hide: false, width: 100 },
        { colId: 'gicsSectorName', hide: false, width: 100 },
        { colId: 'gicsIndustryName', hide: false, width: 100 },
      ],
    },
    tags: [],
    uuid: uuid.v4(),
  },
];

export const SCANNER_PRESET_V2: ScannerConfigV2[] = [
  {
    columns: [
      { name: 'symbol', width: 100 },
      { name: 'name', width: 100 },
      { name: 'price', width: 120 },
      { name: 'change', width: 120 },
      { name: 'changePercent', width: 100 },
      { name: 'dayVolume', width: 100 },
      { name: 'shareFloat', width: 100 },
      { name: 'sharesShortChangePercent', width: 100 },
      { name: 'relativeVolume90Day', width: 100 },
      { name: 'sharesShortPercentOfFloat', width: 100 },
      { name: 'sharesOutstanding', width: 100 },
      { name: 'marketCap', width: 100 },
      { name: 'gicsSectorName', width: 100 },
      { name: 'gicsIndustryName', width: 100 },
    ],
    description: 'Low Float, High Short %, Relative Volume',
    filters: [
      { field: 'relativeVolume90Day', operator: 'bt', parameters: ['2', ''] },
      { field: 'sharesShortChangePercent', operator: 'bt', parameters: ['20', ''] },
      { field: 'shareFloat', operator: 'bt', parameters: ['', '10000000'] },
    ],
    limit: 100,
    name: 'Low Float, High Short %, Relative Volume',
    refreshInterval: -2,
    sortDir: ScannerProtos.SortDir.DESC,
    sortField: 'changePercentClose',
    source: 'stocks',
    uuid: uuid.v4(),
  },
];
